"use client";

import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';

// Memoized components for performance
const MemoizedInlineMath = React.memo(({ math }: { math: string }) => <InlineMath math={math} />);
MemoizedInlineMath.displayName = 'MemoizedInlineMath';

const MemoizedBlockMath = React.memo(({ math }: { math: string }) => <BlockMath math={math} />);
MemoizedBlockMath.displayName = 'MemoizedBlockMath';

const MemoizedImage = React.memo(({ url }: { url: string }) => (
  <div className="my-4 text-center">
    <img
      src={url}
      alt="Exam diagram"
      className="max-w-full h-auto rounded-md border shadow-sm mx-auto"
      loading="lazy"
    />
  </div>
));
MemoizedImage.displayName = 'MemoizedImage';

// Main function to render text with LaTeX
export function renderTextWithLatex(text: string): React.ReactNode {
  if (!text) return null;

  // Normalise double-escaped backslashes from JSON. Literal "\n" newline escapes
  // are handled per text segment (see renderPlainTextWithNewlines) so they never
  // touch math, where a leading \n is a LaTeX command (\neq, \nabla, \nu, ...).
  const processedText = text.replace(/\\\\/g, '\\');

  const imageRegex = /!\[\]\((https:\/\/(?:cdn\.alevels\.fyi|cdn\.ether\.sh|papers\.uat\.ether\.sh|cdn\.percentilelabs\.com|papers\.percentilelabs\.com)\/[^)]+)\)/g;
  const segments: Array<{ type: 'text' | 'image'; content?: string; url?: string }> = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = imageRegex.exec(processedText)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', content: processedText.slice(lastIndex, match.index) });
    }
    segments.push({ type: 'image', url: match[1] });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < processedText.length) {
    segments.push({ type: 'text', content: processedText.slice(lastIndex) });
  }

  if (segments.length === 0) {
    segments.push({ type: 'text', content: processedText });
  }

  const finalParts: React.ReactNode[] = [];
  let partIndex = 0;

  segments.forEach((segment, segmentIndex) => {
    if (segment.type === 'image' && segment.url) {
      finalParts.push(<MemoizedImage key={`image-${segmentIndex}`} url={segment.url} />);
    } else if (segment.type === 'text' && segment.content) {
      const textParts = renderTextSegmentWithLatex(segment.content, partIndex);
      finalParts.push(...textParts);
      partIndex += textParts.length;
    }
  });

  return finalParts.length > 0 ? finalParts : processedText;
}

// Helper for processing text segments with LaTeX and newlines
const renderTextSegmentWithLatex = (text: string, startIndex: number) => {
  if (!text) return [];

  const parts: React.ReactNode[] = [];
  let currentIndex = 0;
  let partIndex = startIndex;
  const displayMathRegex = /\$\$([^$]+)\$\$/g;
  let match: RegExpExecArray | null;

  while ((match = displayMathRegex.exec(text)) !== null) {
    if (match.index > currentIndex) {
      const beforeText = text.slice(currentIndex, match.index);
      parts.push(...renderInlineLatex(beforeText, partIndex));
      partIndex += beforeText.length;
    }

    const mathContent = match[1]
      .replace(/\\\\/g, '\\')
      .replace(/\\/g, '\\')
      .replace(/\text\{([^}]+)\}/g, '\\mathrm{$1}')
      .replace(/\mathbf\{([^}]+)\}/g, '\\boldsymbol{$1}')
      .trim();

    parts.push(
      <div key={`display-${partIndex}`} className="my-6 text-center">
        <MemoizedBlockMath math={mathContent} />
      </div>
    );

    partIndex++;
    currentIndex = match.index + match[0].length;
  }

  if (currentIndex < text.length) {
    const remainingText = text.slice(currentIndex);
    parts.push(...renderInlineLatex(remainingText, partIndex));
  }

  return parts;
};

// Helper for rendering inline LaTeX
const renderInlineLatex = (text: string, startIndex: number) => {
  if (!text) return [];

  const parts: React.ReactNode[] = [];
  let currentIndex = 0;
  let partIndex = startIndex;
  const inlineMathRegex = /\$([^$]+)\$/g;
  let match: RegExpExecArray | null;

  while ((match = inlineMathRegex.exec(text)) !== null) {
    if (match.index > currentIndex) {
      const beforeText = text.slice(currentIndex, match.index);
      parts.push(...renderPlainTextWithNewlines(beforeText, partIndex));
      partIndex++;
    }

    try {
      const mathContent = match[1]
        .replace(/\\\\/g, '\\')
        .replace(/\\/g, '\\')
        .replace(/\text\{([^}]+)\}/g, '\\mathrm{$1}')
        .replace(/\mathbf\{([^}]+)\}/g, '\\boldsymbol{$1}')
        .trim();

      parts.push(<MemoizedInlineMath key={`inline-${partIndex}`} math={mathContent} />);
    } catch (error) {
      console.warn('KaTeX parsing failed for:', match[1], error);
      parts.push(
        <code key={`fallback-${partIndex}`} className="bg-muted px-2 py-1 rounded text-red-600">
          ${match[1]}$
        </code>
      );
    }

    partIndex++;
    currentIndex = match.index + match[0].length;
  }

  if (currentIndex < text.length) {
    const remainingText = text.slice(currentIndex);
    parts.push(...renderPlainTextWithNewlines(remainingText, partIndex));
  }

  return parts;
};

// Bare (undelimited) LaTeX left in content that wasn't wrapped in $...$ — e.g.
// ESAT/ENGAA topic descriptions like "density = \frac{mass}{volume}". Matches a
// backslash command plus any {...}/[...] arguments. Only used on the plain-text
// fallback below, which is reached only for text with no $/$$ delimiters, so
// correctly-delimited content is never touched.
const BARE_LATEX_REGEX = /\\[a-zA-Z]+\*?(?:\{[^{}]*\}|\[[^\]]*\])*/g;

const renderMaybeBareLatex = (line: string, keyBase: string): React.ReactNode[] => {
  // Fast path: no backslash → no bare LaTeX possible, return text unchanged.
  if (!line || line.indexOf('\\') === -1) return [line];

  const out: React.ReactNode[] = [];
  let lastIndex = 0;
  let idx = 0;
  let match: RegExpExecArray | null;
  BARE_LATEX_REGEX.lastIndex = 0;

  while ((match = BARE_LATEX_REGEX.exec(line)) !== null) {
    if (match.index > lastIndex) out.push(line.slice(lastIndex, match.index));
    const math = match[0];
    out.push(
      // On any KaTeX failure, fall back to the original raw string (previous
      // behaviour) rather than showing a red error.
      <InlineMath key={`${keyBase}-m${idx}`} math={math} renderError={() => <>{math}</>} />
    );
    lastIndex = match.index + math.length;
    idx++;
  }
  if (lastIndex < line.length) out.push(line.slice(lastIndex));
  return out;
};

// Splits a non-math text segment into visual lines. Matches real newlines and
// the literal "\n"/"\r\n" escapes that survive in JSON content. This leaf only
// ever receives text outside $...$, so a literal \n here is a newline, not the
// start of a LaTeX command.
const NEWLINE_SPLIT_REGEX = /(?:\\r)?\\n|\r\n|\r|\n/g;

// Helper for rendering plain text with newlines
const renderPlainTextWithNewlines = (text: string, partIndex: number) => {
  if (!text) return [];

  const lines = text.split(NEWLINE_SPLIT_REGEX);

  if (lines.length <= 1) {
    return [
      <span key={`text-${partIndex}`}>
        {renderMaybeBareLatex(text, `text-${partIndex}`)}
      </span>,
    ];
  }

  return [
    <span key={`text-${partIndex}`}>
      {lines.map((line, i) => (
        <span key={i}>
          {renderMaybeBareLatex(line, `text-${partIndex}-${i}`)}
          {i < lines.length - 1 && <br />}
        </span>
      ))}
    </span>
  ];
};

interface LaTeXRendererProps {
  content: string;
  className?: string;
}

function LaTeXRendererComponent({ content, className = "" }: LaTeXRendererProps) {
  return <div className={`latex-content ${className}`}>{renderTextWithLatex(content)}</div>;
}

export default React.memo(LaTeXRendererComponent);